import React, { createContext, useContext, useEffect, useCallback, useMemo, useState } from "react";
import { Box } from "@material-ui/core";
import { DocumentMetadataType } from "../../common/types";
import { DocumentsMain } from "./components/DocumentsMain";

interface DocumentsContextType {
  value?: any;
  docs: DocumentMetadataType[];
  allDocs: DocumentMetadataType[];
  updateSelectedDocType: Function;
  selectedDocType: string;
  userGroups: string[];
  updateDocument: Function;
  updateDocuments: Function;
  documentDownloading: any;
  setDocumentDownloading: any;
  documentHasUploaded: Boolean;
  setDocumentHasUploaded: Function;
}

const DocumentsContext = createContext<DocumentsContextType | undefined>(
  undefined
);
DocumentsContext.displayName = "DocumentsContext";

const Documents = (props: {
  docs: DocumentMetadataType[];
  children: React.ReactNode;
  userGroups: string[];
  setDocs: Function;
}) => {
  const { userGroups } = props;
  const [formattedDocs, setFormattedDocs] = useState<any>({
    "Recent Documents": [],
    "All Documents": [],
  });
  const [selectedDocType, setSelectedDocType] =
    React.useState<string>("Recent Documents");

  const [documentDownloading, setDocumentDownloading] =
    React.useState<boolean>(false);

  const [documentHasUploaded, setDocumentHasUploaded] =
    React.useState<boolean>(false);

  const updateSelectedDocType = (docType: string) => {
    setSelectedDocType(docType);
  };

  const updateDocument = useCallback((doc: DocumentMetadataType, docType: string) => {
    const { documentSk } = doc;
    const filteredDocs = [
      ...formattedDocs[docType].filter(
        (doc: DocumentMetadataType) => doc.documentSk !== documentSk
      ),
      doc,
    ];

    setFormattedDocs({ ...formattedDocs, [docType]: filteredDocs });
  }, [formattedDocs]);

  const updateDocuments = useCallback((
    documents: DocumentMetadataType[],
    docType: string,
  ) => {
    const documentSks = documents.map((doc) => doc.documentSk);
    const filteredDocs = [
      ...formattedDocs[docType].filter((doc: DocumentMetadataType) => {
        return !documentSks.includes(doc.documentSk);
      }),
      ...documents,
    ];

    setFormattedDocs({ ...formattedDocs, [docType]: filteredDocs });
  }, [formattedDocs]);

  useEffect(() => {
    // Get unique document types from array of docs
    const uniqueDocTypes = [
      ...new Set(
        props.docs.map((doc: DocumentMetadataType) => doc.documentTypeName)
      ),
    ];

    const recentDocsFilterTypes: string[] = [
      "Factsheet",
      "Contract Notes",
      "QIR",
      "Investment Statement",
    ];
    const recentDocuments = recentDocsFilterTypes.reduce(
      (recentDocs: DocumentMetadataType[], docType: string) => {
        const filteredRecentDocs = props.docs
          .filter((doc) => doc.documentTypeName === docType)
          .sort((a, b) => {
            const dateA: Date = new Date(a.documentCreatedDatetime);
            const dateB: Date = new Date(b.documentCreatedDatetime);
            return dateB.valueOf() - dateA.valueOf();
          })
          .slice(0, 2);

        return [...recentDocs, ...filteredRecentDocs];
      },
      []
    );

    // create new object with keys as document types
    // where the values are documents filtered on that document type
    const newDocuments = {
      ...uniqueDocTypes.reduce((newDocs, currDocType) => {
        return {
          ...newDocs,
          [currDocType]: props.docs.filter(
            (doc) => doc.documentTypeName === currDocType
          ),
        };
      }, {}),
      "Recent Documents": recentDocuments,
      "All Documents": props.docs,
    };

    setFormattedDocs(newDocuments);
  }, [props.docs]);

  const value: DocumentsContextType = useMemo(() => ({
    docs: formattedDocs[selectedDocType],
    allDocs: formattedDocs,
    updateDocument,
    updateDocuments,
    updateSelectedDocType,
    selectedDocType,
    userGroups,
    documentDownloading,
    setDocumentDownloading,
    documentHasUploaded,
    setDocumentHasUploaded,
  }), [
    formattedDocs,
    documentDownloading,
    documentHasUploaded,
    selectedDocType,
    updateDocument,
    updateDocuments,
    userGroups
  ]);

  return (
    <DocumentsContext.Provider value={value} {...props}>
      <Box
        display="flex"
        style={{ backgroundColor: "transparent", flexDirection: "column" }}
      >
        {props.children}
      </Box>
    </DocumentsContext.Provider>
  );
};

const useDocuments = () => useContext<any>(DocumentsContext);

export { Documents, DocumentsMain, useDocuments };
